import { yupResolver } from '@hookform/resolvers/yup';
import { NavigationPointer01, LayersThree02, MarkerPin01, SwitchVertical01, Truck01 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CommonModal from 'src/components/CommonModal';
import TabButton from 'src/components/TabButton';
import { CURRENCY, DIMENSION_REQUIRED_EQUIPMENT_TYPES, EDIT_QUOTE_TABS, FOOT, KGS_MAX_VALUES, LBS_MAX_VALUES, MEASUREMENT, QUOTE_CLASSIFICATION, QUOTE_URGENCY, STATUS } from 'src/constants/common';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { initialState } from 'src/redux/basic.slice';
import {
    activeTabData,
    addressDetailsData,
    dimensionsData,
    isStatusWonOrLostData,
    isViewQuoteData,
    resetQuoteState,
    servicesData,
    setActiveQuotes,
    setActiveTabQuote,
    setAddressDetails,
    setDimensions,
    setIsQuoteDetailsUpdated,
    setIsShowQuoteDetailModal,
    setIsViewQuote,
    setPredictiveIndex,
    setServices,
    prependActivity,
} from 'src/redux/Quoting.slice';
import { logError } from 'src/services/LoggingService';
import { updateQuote } from 'src/services/QuoteService';
import { calculateLinearFootage, formatLinearFootage, getCalculatedFreightClass, GetAppearance } from 'src/utils/CommonFunctions';
import { createDimensionMaxValidator } from 'src/utils/dimensionValidation';
import WalToast from 'src/utils/WalToast';
import * as yup from 'yup';

import { buildEditStopPayload, IEditStopFormData, stopStepKey, toEditStopFormData, validateItemRouting } from '../AddQuote/multiStop.helpers';
import { WINDOW_TYPE } from '../AddQuote/Steps/addQuoteSteps.const';
import { DANGER_TYPE_LIST, EDIT_QUOTE_ERROR_FIELDS, initialQuoteAction, PACKAGING_GROUP_OPTIONS } from '../quoteDetail.const';
import { createPayloadFromAddressForm } from '../quotingDashboard.helpers';

import ConsigneeDetails from './ConsigneeDetails';
import DimensionsTab from './DimensionsTab';
import EditStopDetails from './EditStopDetails';
import FtlShipmentDetails from './FtlShipmentDetails';
import LinearFootageMismatchModal from './LinearFootageMismatchModal';
import PickupsDropoffsDetails from './PickupsDropoffsDetails';
import { IAddressForm, IQuoteAction, IQuoteDetailModal } from './quoteDetailModal.interface';
import ShipmentDetails from './ShipmentDetails';
import ShipperDetails from './ShipperDetails';
import WeightLinearFootageMismatchModal from './WeightLinearFootageMismatchModal';
import WeightMismatchModal from './WeightMismatchModal';

const QuoteDetailModalNew: FC<IQuoteDetailModal> = ({ handleUpdate = undefined, setChangedByCustomer = undefined }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const isStatusWonOrLost = useSelector(isStatusWonOrLostData);
    const activeTab = useSelector(activeTabData);
    const addressDetails = useSelector(addressDetailsData);
    const dimensionsDetails = useSelector(dimensionsData);
    const services = useSelector(servicesData);
    const isViewQuote = useSelector(isViewQuoteData);
    const { services: allServices, serviceTypes } = useAddressDetails();

    // Hook call triggers API calls to fetch services, vehicle types, etc.
    // Child components access these values from Redux state, not props
    useAddressDetails();

    const [action, setAction] = useState<IQuoteAction>(initialQuoteAction);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);

    // Task 35 (client decision) — Edit Quote: middle-stop edits (address/services/limited access
    // only) live OUTSIDE the flat RHF form (see EditStopDetails.tsx's file header for why) —
    // lifted here so the Save button's dirty-check and the update payload can both reach them.
    // `addressDetails.stops` already rides in via handleOpenQuoteDetailModal's `{...rest}` spread
    // (QuoteDetail/index.tsx) — no new plumbing needed to get it here.
    const [stopsFormData, setStopsFormData] = useState<IEditStopFormData[]>([]);
    const [isStopsDirty, setIsStopsDirty] = useState(false);
    const [stopAddressErrorId, setStopAddressErrorId] = useState<number | null>(null);

    useEffect(() => {
        const rawStops: any[] = addressDetails?.stops || [];

        if (!rawStops.length) {
            setStopsFormData([]);
            setIsStopsDirty(false);

            return;
        }

        // Only middle stops (2..N-1) get their own tab — Stop 1/Final are handled by the existing
        // Shipper/Consignee tabs, whose data the BE now also syncs onto quote_stops automatically.
        const maxPosition = Math.max(...rawStops.map((s: any) => s.position));
        const middleStops = rawStops.filter((s: any) => s.position > 1 && s.position < maxPosition).sort((a: any, b: any) => a.position - b.position);

        setStopsFormData(middleStops.map(toEditStopFormData));
        setIsStopsDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressDetails?.stops]);

    const handleAction = (type: string, data: { [key: string]: any } = {}) => {
        setAction((old) => ({ ...old, [type]: true, data }));
    };

    const handleActionClose = (isTabChange: boolean) => () => {
        setAction(initialQuoteAction);

        if (isTabChange) {
            dispatch(setActiveTabQuote(EDIT_QUOTE_TABS.DIMENSIONS));
        }
    };

    const transformDimensionValue = (value: any, originalValue: any) => {
        if (originalValue === '' || originalValue === null) return null;

        if (typeof originalValue === 'string') {
            const numericValue = parseFloat(originalValue);

            return isNaN(numericValue) ? null : numericValue;
        }

        return value;
    };

    const getIsDimensionRequired = (primaryEquipmentTypeFullName: any) => {
        const fullName = typeof primaryEquipmentTypeFullName === 'string' ? primaryEquipmentTypeFullName : Array.isArray(primaryEquipmentTypeFullName) ? primaryEquipmentTypeFullName[0] : '';

        return DIMENSION_REQUIRED_EQUIPMENT_TYPES.some((type) => fullName?.toLowerCase().includes(type));
    };

    const isDimensionFieldRequired = function (this: yup.TestContext, value: any) {
        const { handlingUnitNo, itemLength, width, height, weight, totalWeight, freightClass } = this.parent;
        const { classification, dimensions, primaryEquipmentTypeFullName } = this.from?.[1]?.value || {};

        // For dry van, reefer, street truck: all line item fields must be > 0
        if (getIsDimensionRequired(primaryEquipmentTypeFullName) && classification !== QUOTE_CLASSIFICATION.FTL) {
            return value !== null && value !== undefined && value >= 1;
        }

        if (dimensions?.length === 1) {
            const hasAnyValue = classification === 'ltl' && (handlingUnitNo >= 1 || itemLength >= 1 || width >= 1 || height >= 1 || weight >= 1 || totalWeight >= 1 || freightClass >= 1);

            if (hasAnyValue) {
                return value !== null && value !== undefined && value >= 1;
            }

            return true;
        }

        if (dimensions?.length > 1 && classification !== QUOTE_CLASSIFICATION.FTL) {
            return value !== null && value !== undefined && value >= 1;
        }

        return true;
    };

    const createMaxValueValidator = (maxValueKey: keyof typeof KGS_MAX_VALUES & keyof typeof LBS_MAX_VALUES, translationKey: string) =>
        function (this: yup.TestContext, value: any) {
            const { weightMeasurement } = this.from?.[1]?.value || {};
            const maxValue = weightMeasurement === MEASUREMENT.WEIGHT2.value ? KGS_MAX_VALUES[maxValueKey] : LBS_MAX_VALUES[maxValueKey];

            if (value === null || value === undefined) return true;

            if (value > maxValue) {
                return this.createError({
                    path: `${this.path}`,
                    message: t(translationKey, { max: maxValue } as any),
                });
            }

            return true;
        };

    const dimMaxValidator = (field: 'HEIGHT' | 'WIDTH') =>
        createDimensionMaxValidator(
            field,
            t,
            (ctx) => {
                const v = ctx.from?.[1]?.value || {};

                return {
                    equipmentType: v.primaryEquipmentType ?? '',
                    equipmentTypeFullName: v.primaryEquipmentTypeFullName ?? '',
                    weightMeasurement: v.weightMeasurement ?? '',
                };
            },
            'EditQuote'
        );

    const appearance = GetAppearance();

    const schema = yup.object().shape({
        classification: yup.string().required(t('ValidationMessages.Service_Type_is_required')),
        urgency: yup.string().required(t('ValidationMessages.Urgency_is_required')),
        pickupDate: yup
            .string()
            .required(t('ValidationMessages.Pickup_Date_is_required'))
            .test('not-in-past', t('QuotingHub.QuotingDashboard.Pickup_date_alert'), function (value) {
                if (!value) return true;

                const currentDateString = moment()
                    .tz(appearance?.timezone || 'UTC')
                    .format('YYYY-MM-DD');
                const pickupDateString = moment.utc(value).startOf('day').format('YYYY-MM-DD');

                const currentDate = moment(currentDateString);
                const pickupDate = moment(pickupDateString);

                return !pickupDate.isBefore(currentDate, 'day');
            }),
        pickupSensitivity: yup.string().required(t('QuotingHub.CreateQuote.pickup_sensitivity_required')),
        // pickupTimeFrame is deprecated — kept nullable to avoid schema errors on old data
        pickupTimeFrame: yup.string().notRequired().nullable(),
        pickupEndDate: yup
            .string()
            .nullable()
            .when('pickupSensitivity', {
                is: 'flexible',
                then: (s) => s.required(t('QuotingHub.CreateQuote.End_Date_Required_For_Flexible')),
                otherwise: (s) => s.notRequired().nullable(),
            }),
        pickupWindowType: yup.string().required(t('QuotingHub.CreateQuote.Pickup_Window_Type_Required')),
        pickupStartTime: yup
            .string()
            .nullable()
            .test('required-when-end-filled', t('QuotingHub.CreateQuote.Pickup_Start_Time_Required'), function (value) {
                const { pickupWindowType, pickupEndTime } = this.parent;

                if (pickupWindowType === 'ready_time' && pickupEndTime) return !!value;

                return true;
            }),
        pickupEndTime: yup
            .string()
            .notRequired()
            .nullable()
            .test('is-after-start', 'End time must be after start time', function (endTime) {
                const { pickupStartTime, pickupWindowType } = this.parent;

                if (pickupWindowType !== 'ready_time') return true;

                if (!pickupStartTime || !endTime) return true;

                const [startHour, startMin] = pickupStartTime.split(':').map(Number);
                const [endHour, endMin] = endTime.split(':').map(Number);
                const startMinutes = startHour * 60 + startMin;
                const endMinutes = endHour * 60 + endMin;

                return endMinutes > startMinutes;
            }),
        deliveryDate: yup
            .string()
            .nullable()
            .test('required-when-end-filled', t('QuotingHub.CreateQuote.Delivery_Start_Date_Required'), function (value) {
                const { deliverySensitivity, deliveryEndDate } = this.parent;

                if (deliverySensitivity === 'flexible' && deliveryEndDate) return !!value;

                return true;
            }),
        deliverySensitivity: yup.string().required(t('QuotingHub.CreateQuote.delivery_sensitivity_required')),
        deliveryEndDate: yup
            .string()
            .nullable()
            .when('deliverySensitivity', {
                is: 'flexible',
                then: (s) =>
                    s.test('required-if-delivery-start-date', t('ValidationMessages.End_Date_is_required'), function (value) {
                        const { deliveryDate } = this.parent;
                        if (deliveryDate && !value) return false;

                        return true;
                    }),
                otherwise: (s) => s.notRequired().nullable(),
            }),
        deliveryWindowType: yup.string().required(t('QuotingHub.CreateQuote.Delivery_Window_Type_Required')),
        deliveryStartTime: yup
            .string()
            .nullable()
            .test('required-when-end-filled', t('QuotingHub.CreateQuote.Delivery_Start_Time_Required'), function (value) {
                const { deliveryWindowType, deliveryEndTime } = this.parent;

                if (deliveryWindowType === WINDOW_TYPE.DELIVERY_TIME && deliveryEndTime) return !!value;

                return true;
            }),
        deliveryEndTime: yup
            .string()
            .notRequired()
            .nullable()
            .test('required-if-delivery-start-time', t('QuotingHub.CreateQuote.Delivery_End_Time_Required'), function (value) {
                const { deliveryWindowType, deliveryStartTime } = this.parent;
                if (deliveryWindowType !== WINDOW_TYPE.DELIVERY_TIME) return true;
                if (deliveryStartTime && !value) return false;

                return true;
            })
            .test('is-after-start', 'End time must be after start time', function (endTime) {
                const { deliveryStartTime, deliveryWindowType } = this.parent;

                if (deliveryWindowType !== WINDOW_TYPE.DELIVERY_TIME) return true;

                if (!deliveryStartTime || !endTime) return true;

                const [startHour, startMin] = deliveryStartTime.split(':').map(Number);
                const [endHour, endMin] = endTime.split(':').map(Number);
                const startMinutes = startHour * 60 + startMin;
                const endMinutes = endHour * 60 + endMin;

                return endMinutes > startMinutes;
            }),
        shipperFullAddress: yup.string().required(t('ValidationMessages.Shipper_address_is_required')),
        consigneeFullAddress: yup.string().required(t('ValidationMessages.Consignee_address_is_required')),
        shipperLatitude: yup.string().required(t('ValidationMessages.Shipper_latitude_is_required')),
        shipperLongitude: yup.string().required(t('ValidationMessages.Shipper_longitude_is_required')),
        consigneeLatitude: yup.string().required(t('ValidationMessages.Consignee_latitude_is_required')),
        consigneeLongitude: yup.string().required(t('ValidationMessages.Consignee_longitude_is_required')),
        shipperCity: yup.string().required(t('ValidationMessages.City_is_required')),
        shipperState: yup.string().required(t('ValidationMessages.State_is_required')),
        shipperCountry: yup.string().required(t('ValidationMessages.Country_is_required')),
        shipperStreetNumber: yup.string().nullable().required(t('ValidationMessages.Street_Number_Required')),
        shipperPostal: yup.string().required(t('ValidationMessages.Postal_Code_Required')),
        shipperCompanyName: yup.string().nullable().required(t('ValidationMessages.Company_name_is_required')),
        consigneeCity: yup.string().required(t('ValidationMessages.City_is_required')),
        consigneeState: yup.string().required(t('ValidationMessages.State_is_required')),
        consigneeCountry: yup.string().required(t('ValidationMessages.Country_is_required')),
        consigneeStreetNumber: yup.string().nullable().required(t('ValidationMessages.Street_Number_Required')),
        consigneePostal: yup.string().required(t('ValidationMessages.Postal_Code_Required')),
        consigneeCompanyName: yup.string().nullable().required(t('ValidationMessages.Company_name_is_required')),
        serviceId: yup.array().of(yup.number()),
        totalWeight: yup.string().test('required-for-ftl-ltl', t('ValidationMessages.Total_weight_is_required'), function (value) {
            const { classification } = this.parent;

            if (classification === 'ftl' || classification === 'ltl') {
                return Boolean(value && value.trim().length > 0);
            }

            return true;
        }),
        linearFootage: yup
            .string()
            .test('required-for-ftl-ltl', t('ValidationMessages.Linear_footage_is_required'), function (value) {
                const { classification } = this.parent;

                if (classification === 'ftl' || classification === 'ltl') {
                    return Boolean(value && value.trim().length > 0);
                }

                return true;
            })
            .test(
                'max',
                t('ValidationMessages.Linear_footage_must_not_exceed', {
                    max: 53,
                } as any),
                function (value) {
                    if (!value || value.trim().length === 0) return true;

                    const numericValue = parseFloat(value);

                    if (isNaN(numericValue)) return true;

                    return numericValue <= 53;
                }
            ),
        primaryEquipmentType: yup.string().required(t('ValidationMessages.Primary_equipment_type_is_required')),
        primaryEquipmentTypeFullName: yup.string().required(t('ValidationMessages.Primary_equipment_type_full_name_is_required')),
        weightMeasurement: yup.string().required(t('ValidationMessages.Weight_measurement_is_required')),
        lengthMeasurement: yup.string().required(t('ValidationMessages.Length_measurement_is_required')),
        dimensions: yup
            .array()
            .test('min-required', 'At least one line item is required', function (value) {
                const { primaryEquipmentTypeFullName } = this.parent;

                if (getIsDimensionRequired(primaryEquipmentTypeFullName)) {
                    return Array.isArray(value) && value.length >= 1;
                }

                return true;
            })
            .of(
                yup.object().shape({
                    handlingUnit: yup
                        .number()
                        .transform((value, originalValue) => (originalValue === '' ? null : value))
                        .nullable(),

                    handlingUnitNo: yup
                        .number()
                        .transform((value, originalValue) => (originalValue === '' ? null : value))
                        .nullable()
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    itemLength: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .max(9999)
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    width: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('max', '', dimMaxValidator('WIDTH'))
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    height: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('max', '', dimMaxValidator('HEIGHT'))
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    weight: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    totalWeight: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('max', '', createMaxValueValidator('MAX_WEIGHT', 'ValidationMessages.Total_weight_must_not_exceed'))
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),

                    freightClass: yup
                        .number()
                        .transform(transformDimensionValue)
                        .nullable()
                        .test('conditional-required', t('ValidationMessages.This_field_is_required_when_any_dimension_field_is_filled'), isDimensionFieldRequired),
                    commodity: yup
                        .string()
                        .transform((value, originalValue) => (originalValue === '' ? null : value))
                        .nullable(),
                    isStack: yup.boolean().nullable(),
                })
            ),
        insuranceCommodity: yup
            .array()
            .of(yup.string())
            .default([])
            .test('commodity-required-when-enabled', 'Commodity is required', function (value) {
                const { isLoadInsuranceEnabled } = this.parent;

                if (isLoadInsuranceEnabled) {
                    return Array.isArray(value) && value.length > 0;
                }

                return true;
            }),
        insuranceCommercialValue: yup
            .number()
            .transform((value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) {
                    return null;
                }

                return value;
            })
            .nullable()
            .test('value-required-when-enabled', t('QuotingHub.CreateQuote.Insurance_commercial_value_is_required_when_load_insurance_is_enabled'), function (value) {
                const { isLoadInsuranceEnabled } = this.parent;

                // If toggle is enabled, commercial value is required
                if (isLoadInsuranceEnabled) {
                    return value !== null && value !== undefined && typeof value === 'number' && !isNaN(value) && value > 0;
                }

                return true;
            })
            .test('positive-number', t('QuotingHub.CreateQuote.Insurance_commercial_value_must_be_a_positive_number'), function (value) {
                // Only validate if value exists
                if (value === null || value === undefined || isNaN(value) || value === 0) {
                    return true;
                }

                return value > 0;
            }),
        specialNotes: yup.string().max(200, 'Special notes cannot exceed 200 characters').nullable().default(''),
    });

    const defaultValues = useMemo(() => {
        let processedServiceIds: any[] = [];
        let processedLimitedAccessType = '';
        let processedLimitedAccess = false;
        let processedLimitedAccessOther = '';
        let inbondData = {
            inbond: false,
            inbondType: '',
            inbondName: '',
            inbondAddress: '',
            inbondContactMethod: '',
            inbondContactValue: '',
        };
        let dangerousData = {
            dangerousGoods: false,
            dangerGoodsType: DANGER_TYPE_LIST[0].value,
            unitedNationsNumber: '',
            packagingGroup: PACKAGING_GROUP_OPTIONS[0].value,
            goodsClass: '',
            description: '',
            emergencyContactName: '',
            emergencyContactNumber: '',
            emergencyContactExt: '',
            emergencyEapnbr: '1-888-CAN-UTEC',
        };
        let lengthMeasurement = MEASUREMENT.LENGTH1.value;
        let weightMeasurement = MEASUREMENT.WEIGHT1.value;

        if (services) {
            processedServiceIds = services.map((val: any) => {
                if (val?.service?.slug === 'limited_access_delivery') {
                    processedLimitedAccess = true;
                    processedLimitedAccessType = val.limitedAccessType;
                    processedLimitedAccessOther = val.limitedAccessOther;
                }

                if (val?.service?.slug === 'in-bond') {
                    inbondData.inbond = true;
                    inbondData.inbondType = val.inbondType;
                    inbondData.inbondName = val.inbondName;
                    inbondData.inbondAddress = val.inbondAddress;
                    inbondData.inbondContactMethod = val.inbondContactMethod;
                    inbondData.inbondContactValue = val.inbondContactValue;
                }

                if (val?.service?.slug === 'dangerous_goods') {
                    dangerousData.dangerousGoods = true;
                    dangerousData.dangerGoodsType = val.dangerGoodsType;
                    dangerousData.unitedNationsNumber = val.unitedNationsNumber;
                    dangerousData.packagingGroup = val.packagingGroup;
                    dangerousData.goodsClass = val.goodsClass;
                    dangerousData.description = val.description;
                    dangerousData.emergencyContactName = val.emergencyContactName;
                    dangerousData.emergencyContactNumber = val.emergencyContactNumber;
                    dangerousData.emergencyContactExt = val.emergencyContactExt;
                    dangerousData.emergencyEapnbr = val.emergencyEapnbr;
                }

                return val.serviceId;
            });
        }

        let dimensionData: any = [];

        if (dimensionsDetails && dimensionsDetails.length) {
            weightMeasurement = dimensionsDetails[0]?.weightMeasurement;
            lengthMeasurement = dimensionsDetails[0]?.lengthMeasurement;

            dimensionData = dimensionsDetails.map((dimension: any) => {
                let freightClass = dimension.freightClass;
                let itemClass = dimension.itemClassForDayross;

                if (!freightClass || !itemClass) {
                    const freightData = getCalculatedFreightClass({
                        itemLength: dimension.itemLength,
                        width: dimension.width,
                        height: dimension.height,
                        weight: dimension.weight,
                        lengthUnit: dimension.lengthMeasurement,
                        weightUnit: dimension.weightMeasurement,
                    });

                    freightClass = freightData?.freightClass;
                    itemClass = freightData?.itemClassForDayross;
                }

                return {
                    id: dimension.id,
                    handlingUnit: dimension.handlingUnit || (initialState.handlingUnitOptions.length > 0 ? initialState.handlingUnitOptions[0].value : ''),
                    handlingUnitNo: dimension.handlingUnitNo || 0,
                    weight: dimension.weight || 0,
                    itemLength: dimension.itemLength || 0,
                    width: dimension.width || 0,
                    height: dimension.height || 0,
                    totalWeight: dimension.totalWeight || 0,
                    isStack: dimension.isStack || false,
                    commodity: dimension?.commodity || null,
                    freightClass: freightClass || 0,
                    itemClassForDayross: itemClass || 0,
                    // Task 37 (bug fix) — this whitelist previously dropped pickupStopId/dropStopId
                    // before they ever reached the RHF form, so DimensionsTab.tsx's backfill effect
                    // had nothing to read and every row silently fell back to its `?? 1`/`?? stopCount`
                    // defaults (every line item showing Stop 1 -> last stop, regardless of what was
                    // actually saved at create time).
                    pickupStopId: dimension.pickupStopId ?? null,
                    dropStopId: dimension.dropStopId ?? null,
                };
            });
        } else {
            let dimensions = [
                {
                    id: null,
                    handlingUnit: 1,
                    handlingUnitNo: 0,
                    weight: 0,
                    itemLength: 0,
                    width: 0,
                    height: 0,
                    totalWeight: 0,
                    isStack: true,
                    commodity: null,
                    freightClass: 0,
                    itemClassForDayross: 0,
                },
            ];
            dimensionData = [...dimensions];
        }
        const shipperAddressObj = addressDetails?.shipperLatitude
            ? {
                  fullAddress: addressDetails.shipperFullAddress || '',
                  address1: addressDetails.shipperAddress1 || '',
                  city: addressDetails.shipperCity || '',
                  state: addressDetails.shipperState || '',
                  country: addressDetails.shipperCountry || '',
                  postal: addressDetails.shipperPostal || '',
                  latitude: addressDetails.shipperLatitude || '',
                  longitude: addressDetails.shipperLongitude || '',
                  stateCode: addressDetails.shipperStateCode || '',
                  countryCode: addressDetails.shipperCountryCode || '',
                  streetNumber: addressDetails.shipperStreetNumber || '',
                  companyName: addressDetails.shipperCompanyName || '',
              }
            : null;

        const consigneeAddressObj = addressDetails?.consigneeLatitude
            ? {
                  fullAddress: addressDetails.consigneeFullAddress || '',
                  address1: addressDetails.consigneeAddress1 || '',
                  city: addressDetails.consigneeCity || '',
                  state: addressDetails.consigneeState || '',
                  country: addressDetails.consigneeCountry || '',
                  postal: addressDetails.consigneePostal || '',
                  latitude: addressDetails.consigneeLatitude || '',
                  longitude: addressDetails.consigneeLongitude || '',
                  stateCode: addressDetails.consigneeStateCode || '',
                  countryCode: addressDetails.consigneeCountryCode || '',
                  streetNumber: addressDetails.consigneeStreetNumber || '',
                  companyName: addressDetails.consigneeCompanyName || '',
              }
            : null;

        return {
            classification: addressDetails?.classification || '',
            urgency: QUOTE_URGENCY.LIVE,
            pickupDate: addressDetails?.pickupDate || '',
            pickupSensitivity: addressDetails?.pickupSensitivity || 'fixed',
            pickupTimeFrame: null, // deprecated — replaced by pickupSensitivity fixed/flexible + pickupEndDate
            pickupEndDate: addressDetails?.pickupEndDate || null,
            pickupWindowType: addressDetails?.pickupWindowType || 'ready_time',
            pickupStartTime: addressDetails?.pickupStartTime || null,
            pickupEndTime: addressDetails?.pickupEndTime || null,
            deliveryDate: addressDetails?.deliveryDate || null,
            deliverySensitivity: addressDetails?.deliverySensitivity || 'open',
            deliveryEndDate: addressDetails?.deliveryEndDate || null,
            deliveryWindowType: addressDetails?.deliveryWindowType || WINDOW_TYPE.DELIVERY_TIME,
            deliveryStartTime: addressDetails?.deliveryStartTime || null,
            deliveryEndTime: addressDetails?.deliveryEndTime || null,
            shipperFullAddress: addressDetails?.shipperFullAddress || '',
            shipperAddress1: addressDetails?.shipperAddress1 || null,
            shipperStreetNumber: addressDetails?.shipperStreetNumber || null,
            shipperCity: addressDetails?.shipperCity || null,
            shipperState: addressDetails?.shipperState || null,
            shipperCountry: addressDetails?.shipperCountry || null,
            shipperPostal: addressDetails?.shipperPostal || null,
            shipperLatitude: addressDetails?.shipperLatitude || null,
            shipperLongitude: addressDetails?.shipperLongitude || null,
            shipperStateCode: addressDetails?.shipperStateCode || null,
            shipperCountryCode: addressDetails?.shipperCountryCode || null,
            shipperCompanyName: addressDetails?.shipperCompanyName || null,
            shipperId: addressDetails?.shipperId || null,
            id: addressDetails?.shipperId || null,
            companyName: addressDetails?.shipperCompanyName || null,
            shipperAddress: shipperAddressObj,
            consigneeFullAddress: addressDetails?.consigneeFullAddress || '',
            consigneeAddress1: addressDetails?.consigneeAddress1 || null,
            consigneeStreetNumber: addressDetails?.consigneeStreetNumber || null,
            consigneeCity: addressDetails?.consigneeCity || null,
            consigneeState: addressDetails?.consigneeState || null,
            consigneeCountry: addressDetails?.consigneeCountry || null,
            consigneePostal: addressDetails?.consigneePostal || null,
            consigneeLatitude: addressDetails?.consigneeLatitude || null,
            consigneeLongitude: addressDetails?.consigneeLongitude || null,
            consigneeStateCode: addressDetails?.consigneeStateCode || null,
            consigneeCountryCode: addressDetails?.consigneeCountryCode || null,
            consigneeCompanyName: addressDetails?.consigneeCompanyName || null,
            consigneeId: addressDetails?.consigneeId || null,
            consigneeAddress: consigneeAddressObj,
            serviceId: processedServiceIds,
            limitedAccessType: processedLimitedAccessType,
            limitedAccess: processedLimitedAccess,
            limitedAccessOther: processedLimitedAccessOther,
            totalWeight: addressDetails?.totalWeight || '',
            linearFootage: addressDetails?.linearFootage || '',
            primaryEquipmentTypeFullName: addressDetails?.primaryEquipmentTypeFullName || '',
            primaryEquipmentType: addressDetails?.primaryEquipmentType || '',
            secondaryEquipmentTypeFullNames: addressDetails?.secondaryEquipmentTypeFullNames
                ? addressDetails.secondaryEquipmentTypeFullNames.split(',').filter((item: string) => item.trim() !== '')
                : [],
            secondaryEquipmentTypes: addressDetails?.secondaryEquipmentTypes ? addressDetails.secondaryEquipmentTypes.split(',').filter((item: string) => item.trim() !== '') : [],
            interModalType: addressDetails?.interModalType || 'both',
            inbond: inbondData.inbond,
            inbondType: inbondData.inbondType,
            inbondName: inbondData.inbondName,
            inbondAddress: inbondData.inbondAddress,
            inbondContactMethod: inbondData.inbondContactMethod,
            inbondContactValue: inbondData.inbondContactValue,
            // Treat "0" and 0 as no insurance value — the DB can store "0" as a string default
            // which is truthy and would incorrectly enable the insurance toggle
            insuranceCommercialValue: addressDetails?.insuranceCommercialValue && Number(addressDetails?.insuranceCommercialValue) > 0 ? String(addressDetails.insuranceCommercialValue) : '',
            insuranceCurrencyType: addressDetails?.insuranceCurrencyType ?? initialState.currency ?? CURRENCY.CAD,
            insuranceCommodity: addressDetails?.insuranceCommodity ? addressDetails.insuranceCommodity.split('|').filter((item: string) => item.trim() !== '') : [],
            isLoadInsuranceEnabled: Boolean(addressDetails?.isLoadInsuranceEnabled),
            dangerousGoods: dangerousData.dangerousGoods,
            dangerGoodsType: dangerousData.dangerGoodsType,
            unitedNationsNumber: dangerousData.unitedNationsNumber,
            packagingGroup: dangerousData.packagingGroup,
            goodsClass: dangerousData.goodsClass,
            description: dangerousData.description,
            emergencyContactName: dangerousData.emergencyContactName,
            emergencyContactNumber: dangerousData.emergencyContactNumber,
            emergencyContactExt: dangerousData.emergencyContactExt,
            emergencyEapnbr: dangerousData.emergencyEapnbr,
            dimensions: dimensionData,
            weightMeasurement,
            lengthMeasurement,
            specialNotes: addressDetails?.specialNotes || '',
        };
    }, [addressDetails, services]);

    const methods = useForm<IAddressForm>({
        resolver: yupResolver(schema) as unknown as Resolver<IAddressForm>,
        defaultValues,
    });
    const {
        handleSubmit,
        watch,
        setValue,
        reset,
        trigger,
        formState: { isSubmitted },
    } = methods;

    // Reset the form whenever a different quote is loaded (addressDetails.id changes),
    // mirroring the reference implementation's pattern to handle async data updates.
    const prevAddressDetailsIdRef = useRef<number | null>(null);
    useEffect(() => {
        if (!addressDetails?.id) {
            return;
        }

        const currentId = addressDetails.id;

        if (prevAddressDetailsIdRef.current !== currentId) {
            prevAddressDetailsIdRef.current = currentId;
            reset(defaultValues);
        }
    }, [addressDetails?.id, defaultValues, reset]);

    const classification = watch('classification');
    const formValues = watch();
    const watchedPrimaryEquipmentType = watch('primaryEquipmentType');
    const watchedWeightMeasurement = watch('weightMeasurement');

    // Task 40 (client decision) — the stop's activity is now a real (session-local) editable hint
    // on `stopsFormData` itself (see EditStopDetails.tsx), not derived from routing — so the
    // unitsOn/unitsOff summary this used to feed into that badge is no longer needed here.
    const stopCountForSummary = addressDetails?.stops?.length || 2;

    // Equipment type / weight unit drives the per-equipment HEIGHT/WIDTH cap. When the user changes
    // either on a sibling tab (Shipment Details), stale dim errors stick because Yup only re-runs on
    // field interaction. Re-trigger every dim row's height/width validation so messages refresh.
    const dimensionsCount = formValues?.dimensions?.length ?? 0;

    useEffect(() => {
        if (dimensionsCount === 0) return;

        const fields: string[] = [];

        for (let i = 0; i < dimensionsCount; i += 1) {
            fields.push(`dimensions.${i}.height`, `dimensions.${i}.width`);
        }

        if (isSubmitted) {
            trigger(fields as any);
        }
    }, [watchedPrimaryEquipmentType, watchedWeightMeasurement, dimensionsCount, trigger]);

    const normalizeValue = (value: any): any => {
        if (value === null || value === undefined || value === '') return null;

        if (moment.isMoment(value)) {
            return value.format('YYYY-MM-DDT00:00:00.000Z');
        }

        // Date objects (from date pickers) — normalize to YYYY-MM-DD for dirty comparison
        if (value instanceof Date) {
            return moment(value).format('YYYY-MM-DD');
        }

        // DB date strings like "2026-04-22 00:00:00" or "2026-04-22T00:00:00.000Z" — normalize to YYYY-MM-DD
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[\sT]/.test(value)) {
            const parsed = moment.utc(value);
            if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            return Object.keys(value).reduce((acc, key) => {
                acc[key] = normalizeValue(value[key]);

                return acc;
            }, {} as any);
        }

        if (Array.isArray(value)) {
            const normalized = value.map(normalizeValue);

            if (normalized.length === 1 && (normalized[0] === '' || normalized[0] === null)) {
                return [];
            }

            return normalized;
        }

        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '')) {
            return String(value);
        }

        return value;
    };

    const isFormDirty = useMemo(() => {
        const normalizedFormValues = normalizeValue(formValues);
        const normalizedDefaultValues = normalizeValue(defaultValues);

        const fieldsToExclude = [
            'shipperCompanyName',
            'shipperAddress1',
            'shipperAddress',
            'shipperStreetNumber',
            'shipperCity',
            'shipperState',
            'shipperCountry',
            'shipperPostal',
            'shipperStateCode',
            'shipperCountryCode',
            'shipperLatitude',
            'shipperLongitude',
            // consigneeCompanyName is intentionally NOT excluded — editing a consignee's
            // company name must mark the form dirty to enable "Save Changes".
            'consigneeAddress1',
            'consigneeAddress',
            'consigneeStreetNumber',
            'consigneeCity',
            'consigneeState',
            'consigneeCountry',
            'consigneePostal',
            'consigneeStateCode',
            'consigneeCountryCode',
            'consigneeLatitude',
            'consigneeLongitude',
            'id',
            // companyName (shipper) and consigneeCompanyName are intentionally NOT excluded
            // so that editing either name marks the form dirty and enables "Save Changes".
            // shipperId and consigneeId are intentionally NOT excluded so that selecting a
            // different shipper/consignee also marks the form dirty.
            'insuranceCommercialValueUSD',
        ];

        const normalizeNumber = (val: any) => {
            if (!val || val === '') return '';

            const num = parseFloat(val);

            return isNaN(num) ? val : num;
        };

        const excludeFields = (obj: any) => {
            const result = { ...obj };

            fieldsToExclude.forEach((field) => {
                delete result[field];
            });

            if (result.totalWeight) result.totalWeight = normalizeNumber(result.totalWeight);

            return result;
        };

        const formValuesToCompare = excludeFields(normalizedFormValues);
        const defaultValuesToCompare = excludeFields(normalizedDefaultValues);

        return JSON.stringify(formValuesToCompare) !== JSON.stringify(defaultValuesToCompare);
    }, [formValues, defaultValues]);

    const hasOnlyUrgencyOrSecondaryEquipmentChanged = useMemo(() => {
        const normalizedFormValues = normalizeValue(formValues);
        const normalizedDefaultValues = normalizeValue(defaultValues);

        const fieldsToExclude = [
            'shipperCompanyName',
            'shipperAddress1',
            'shipperAddress',
            'shipperStreetNumber',
            'shipperCity',
            'shipperState',
            'shipperCountry',
            'shipperPostal',
            'shipperStateCode',
            'shipperCountryCode',
            'shipperLatitude',
            'shipperLongitude',
            'consigneeCompanyName',
            'consigneeAddress1',
            'consigneeAddress',
            'consigneeStreetNumber',
            'consigneeCity',
            'consigneeState',
            'consigneeCountry',
            'consigneePostal',
            'consigneeStateCode',
            'consigneeCountryCode',
            'consigneeLatitude',
            'consigneeLongitude',
            'id',
            'companyName',
            'shipperId',
            'consigneeId',
            'insuranceCommercialValueUSD',
        ];

        const excludeFields = (obj: any) => {
            const result = { ...obj };

            fieldsToExclude.forEach((field) => {
                delete result[field];
            });

            return result;
        };

        const formValuesToCompare = excludeFields(normalizedFormValues);
        const defaultValuesToCompare = excludeFields(normalizedDefaultValues);

        // Create copies without the fields we want to ignore
        const fieldsToIgnore = ['urgency', 'secondaryEquipmentTypes', 'secondaryEquipmentTypeFullNames'];
        const formWithoutIgnored = { ...formValuesToCompare };
        const defaultsWithoutIgnored = { ...defaultValuesToCompare };

        fieldsToIgnore.forEach((field) => {
            delete formWithoutIgnored[field];
            delete defaultsWithoutIgnored[field];
        });

        // If forms are equal after removing ignored fields, then only those fields changed
        return JSON.stringify(formWithoutIgnored) === JSON.stringify(defaultsWithoutIgnored) && JSON.stringify(formValuesToCompare) !== JSON.stringify(defaultValuesToCompare);
    }, [formValues, defaultValues]);

    const handleUpdateQuoteDetails = (data: any) => {
        const payload = {
            ...data,
            shipperId: data.id,
            consigneeId: data.consigneeId,
        };
        // Task 35 — middle-stop edits ride alongside (not inside) the RHF-derived payload; Stop
        // 1/Final's quote_stops sync happens automatically server-side from the same
        // address.shipper*/consignee* fields already being sent, no extra field needed for those.
        // Bug fix (user-reported) — a stale Pickup-only/Delivery-only service (from before the hint
        // was switched) must only be dropped here, at save time, using the FINAL hint — never while
        // the user is merely browsing Stop Activity pills in EditStopDetails.tsx (see its
        // handleHintChange comment). Mirrors EditStopDetails.tsx's own NON_STOP_SUPPORTED_SLUGS
        // filtering — in-bond/load_insurance/dangerous_goods live under serviceTypes.OTHER, not
        // PICKUP/DELIVERY, so this exclusion is normally a no-op, kept only for parity/safety.
        const NON_STOP_SUPPORTED_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods'];
        const pickupServiceIds = (serviceTypes?.PICKUP || []).filter((s: any) => !NON_STOP_SUPPORTED_SLUGS.includes(s.slug)).map((s: any) => s.id);
        const deliveryServiceIds = (serviceTypes?.DELIVERY || []).filter((s: any) => !NON_STOP_SUPPORTED_SLUGS.includes(s.slug)).map((s: any) => s.id);
        const apiPayload = {
            ...createPayloadFromAddressForm(payload),
            stops: stopsFormData.map((stop) => buildEditStopPayload(stop, { pickupServiceIds, deliveryServiceIds })),
        };
        updateQuote(addressDetails.id, apiPayload)
            .then((result: any) => {
                // Interceptor unwraps response: result = { status, message, data: { quote, dimensions, services, hasQuoteDataChanged, stops } }
                const responseData = result?.data;

                if (handleUpdate) {
                    handleUpdate(result?.data);
                } else {
                    if (!hasOnlyUrgencyOrSecondaryEquipmentChanged && apiPayload.address.classification === QUOTE_CLASSIFICATION.LTL) {
                        dispatch(setIsQuoteDetailsUpdated(true));
                    }
                    // Includes the updated `stops` (Task 35) so Stops Summary/Map-It on the Quote
                    // Detail page reflect the change immediately — no page reload.
                    dispatch(setAddressDetails({ ...responseData?.quote, stops: responseData?.stops }));
                    dispatch(setDimensions(responseData?.dimensions));
                    dispatch(setServices(responseData?.services));
                }

                if (responseData?.hasQuoteDataChanged && responseData?.quote?.status === STATUS.OPENED) {
                    dispatch(setActiveQuotes([]));

                    if (setChangedByCustomer) {
                        setChangedByCustomer(true);
                    }
                }
                dispatch(setIsShowQuoteDetailModal(false));
                dispatch(setPredictiveIndex(responseData?.predictiveIndexData));

                if (responseData?.activity) {
                    dispatch(prependActivity(responseData.activity));
                }
            })
            .catch(async (error: any) => {
                await logError(error, {
                    errorMessage: error?.message || 'Error while updating quote',
                    errorLocation: 'EditQuoteDetail: updateQuote',
                    storeInDatabase: true,
                    logToConsole: true,
                });

                const errMsg = error?.response?.status === 409 ? error?.response?.data?.message : t('QuotingHub.QuotingDashboard.Dimensions_create_quote_error');
                WalToast.error(errMsg);
            })
            .finally(() => {
                setIsUpdateLoading(false);
            });
    };

    const onSubmit = (data: IAddressForm) => {
        setIsUpdateLoading(true);

        // tailgate check
        let isTotalWeightOver = false;
        let isItemLengthOver = false;
        const isTailgate = data.serviceId.some((serviceId: number) =>
            allServices?.some((service: any) => service.id === serviceId && (service.slug === 'tailgate_pickup' || service.slug === 'tailgate_delivery'))
        );
        const isActionTrue = action.tailgate || action.weightLinearFootageMismatch || action.weightMismatch || action.linearFootageMismatch;

        if (data?.classification === QUOTE_CLASSIFICATION?.LTL && !isActionTrue) {
            let dimensionsToCheck = data.dimensions;
            let totalWeight = 0;

            dimensionsToCheck.forEach((dimension: any) => {
                totalWeight += dimension.totalWeight;
                const maxLengthOver = data?.weightMeasurement === MEASUREMENT.WEIGHT1.value ? LBS_MAX_VALUES.ITEM_LENGTH_OVER : KGS_MAX_VALUES.ITEM_LENGTH_OVER;

                if (dimension.itemLength >= maxLengthOver) {
                    isItemLengthOver = true;
                }
            });

            isTotalWeightOver = data.weightMeasurement === MEASUREMENT.WEIGHT1?.value ? totalWeight >= LBS_MAX_VALUES.TAILGATE_MAX_WEIGHT : totalWeight >= KGS_MAX_VALUES.TAILGATE_MAX_WEIGHT;
        }

        const isTailgateRequired = isTailgate && (isTotalWeightOver || isItemLengthOver) && data?.classification === QUOTE_CLASSIFICATION?.LTL;
        const totalDimensionsWeight = data?.dimensions ? data?.dimensions.reduce((sum: any, item: any) => +sum + (+item.totalWeight || 0), 0) : 0;

        if (isTailgateRequired && !isActionTrue) {
            handleAction('tailgate', {
                weightMeasurement: data.weightMeasurement,
            });
            setIsUpdateLoading(false);

            return;
        }

        // weight and linear footage check
        const isWeightMismatch = data?.classification === QUOTE_CLASSIFICATION?.LTL && data.totalWeight && totalDimensionsWeight && totalDimensionsWeight != +data.totalWeight;
        const totalDimensionsLinearFootageData =
            formatLinearFootage(calculateLinearFootage(data?.dimensions, data?.primaryEquipmentType, false, MEASUREMENT.WEIGHT1.value, data?.weightMeasurement as any)) || undefined;
        const isLinearFootageMismatch =
            data?.classification === QUOTE_CLASSIFICATION?.LTL && data.linearFootage && totalDimensionsLinearFootageData && totalDimensionsLinearFootageData != data.linearFootage;

        if (isWeightMismatch && isLinearFootageMismatch) {
            handleAction('weightLinearFootageMismatch', {
                totalWeight: totalDimensionsWeight,
                linearFootage: totalDimensionsLinearFootageData,
                weightMeasurement: data.weightMeasurement,
            });
            setIsUpdateLoading(false);

            return;
        }

        if (isWeightMismatch) {
            handleAction('weightMismatch', {
                totalWeight: totalDimensionsWeight,
                weightMeasurement: data.weightMeasurement,
            });
            setIsUpdateLoading(false);

            return;
        }

        if (isLinearFootageMismatch) {
            handleAction('linearFootageMismatch', {
                linearFootage: totalDimensionsLinearFootageData,
                weightMeasurement: data.weightMeasurement,
            });
            setIsUpdateLoading(false);

            return;
        }

        // Task 35 — middle-stop address is required (mirrors StopDetails.tsx's own
        // `!formData.stopAddress?.fullAddress` check), done manually here since stops live outside
        // the yup schema. Switches to the offending stop's tab so the error is visible.
        const invalidStop = stopsFormData.find((stop) => !stop.stopAddress?.fullAddress);

        if (invalidStop) {
            setStopAddressErrorId(invalidStop.id);
            dispatch(setActiveTabQuote(stopStepKey(invalidStop.position)));
            setIsUpdateLoading(false);

            return;
        }

        // Task 37 (client decision) — item pick/drop routing, same rules as Add Quote's Dimensions
        // step (drop-after-pick, stop 1 must board, stop N must receive, every middle stop needs
        // activity) — checked here so the rep gets an inline toast instead of a round trip to the
        // BE validator. No-ops for classic quotes (stopCountForSummary defaults to 2 there).
        if (stopCountForSummary > 2) {
            const routing = validateItemRouting({ stopCount: stopCountForSummary, items: data.dimensions });

            if (!routing.valid) {
                WalToast.error(t(routing.errors[0].messageKey, routing.errors[0].n ? ({ n: routing.errors[0].n } as any) : undefined) as string);
                setIsUpdateLoading(false);

                return;
            }
        }

        handleUpdateQuoteDetails(data);
    };

    const handleClose = () => {
        if (handleUpdate) {
            dispatch(resetQuoteState());
        } else {
            dispatch(setIsShowQuoteDetailModal(false));
            dispatch(setIsViewQuote(true));
            dispatch(setActiveTabQuote(EDIT_QUOTE_TABS.SHIPPER_DETAILS));
        }
    };

    const handleTabChange = (e: any) => {
        dispatch(setActiveTabQuote(e.target.dataset.value));
    };

    const getTabForErrorKey = useCallback(
        (key: string) => {
            if (EDIT_QUOTE_ERROR_FIELDS.SHIPPER_DETAILS.includes(key)) {
                return EDIT_QUOTE_TABS.SHIPPER_DETAILS;
            }

            if (EDIT_QUOTE_ERROR_FIELDS.CONSIGNEE_DETAILS.includes(key)) {
                return EDIT_QUOTE_TABS.CONSIGNEE_DETAILS;
            }

            if (EDIT_QUOTE_ERROR_FIELDS.FTL.includes(key) && classification === QUOTE_CLASSIFICATION.FTL) {
                return EDIT_QUOTE_TABS.SHIPMENT_DETAILS;
            }

            if (EDIT_QUOTE_ERROR_FIELDS.SHIPMENT_DETAILS.includes(key)) {
                return EDIT_QUOTE_TABS.SHIPMENT_DETAILS;
            }

            if (key.startsWith('dimensions') || EDIT_QUOTE_ERROR_FIELDS.DIMENSIONS.includes(key)) {
                return EDIT_QUOTE_TABS.DIMENSIONS;
            }

            return null;
        },
        [classification]
    );

    const handleErrorsAndSetTab = useCallback(
        (err: any) => {
            const errorKeys = Object.keys(err);

            const currentTabHasErrors = errorKeys.some((key) => getTabForErrorKey(key) === activeTab);

            if (currentTabHasErrors) {
                return;
            }

            const targetTab = errorKeys.map(getTabForErrorKey).find((tab) => tab !== null);

            if (targetTab) {
                dispatch(setActiveTabQuote(targetTab));
            }
        },
        [dispatch, activeTab, getTabForErrorKey]
    );

    const handleError = (err: any) => handleErrorsAndSetTab(err);

    const handleMismatch = (fields: (keyof IAddressForm)[]) => () => {
        if (action.data) {
            fields.forEach((field) => {
                setValue(field as any, action.data?.[field]);
            });
        }
        handleActionClose(false)();
        handleSubmit(onSubmit, handleError)();
    };

    const handleTailgate = (status: boolean) => () => {
        setAction(initialQuoteAction);

        if (status) {
            handleSubmit(onSubmit, handleError)();
        } else {
            setIsUpdateLoading(false);
        }
    };

    const handleViewQuote = () => {
        dispatch(setIsViewQuote(false));
    };

    // Tab structure mirrors QUOTE_STEPS from createQuote.const.ts:
    // FTL → 3 tabs (Address, Quote Detail, Load & Shipment Details)
    // LTL/Volume LTL → 4 tabs (Address, Quote Detail, Set Shipment Details, Dimensions)
    const QUOTE_DETAIL_TABS = useMemo(() => {
        // Task 35 (client decision) — one tab per middle stop, inserted between Shipper Details
        // and Consignee Details, mirroring the Add Quote wizard's per-stop steps. Empty for
        // classic (non-multi-stop) quotes.
        // Bug fix (user-reported) — label is the RELATIVE middle-stop number (1-based, matching
        // Add Quote's wizard: first middle stop is always "Stop 1"), not the absolute route
        // position. stopsFormData only ever holds middle stops, already sorted by position, so
        // array index + 1 is exactly that relative number.
        // Client decision (Final wording) — same as Add Quote's sidebar: plain "Stop N" until the
        // stop's address is entered, then "Stop N · City, Prov".
        const stopTabs = stopsFormData.map((stop, index) => {
            const city = stop.stopAddress?.city;
            const stateCode = stop.stopAddress?.stateCode || stop.stopAddress?.state;

            return {
                value: stopStepKey(stop.position),
                name: city ? t('MyQuote.Stop_N_With_Location', { n: index + 1, city, stateCode: stateCode || '' }) : t('MyQuote.Stop_N', { n: index + 1 }),
                icon: <MarkerPin01 className="vertical-secondary-tab-item-icon flex-none" />,
            };
        });

        const baseTabs = [
            {
                value: EDIT_QUOTE_TABS.SHIPPER_DETAILS,
                name: t('Common.Shipper_Details'),
                icon: <MarkerPin01 className="vertical-secondary-tab-item-icon flex-none" />,
            },
            ...stopTabs,
            {
                value: EDIT_QUOTE_TABS.CONSIGNEE_DETAILS,
                name: t('Common.Consignee_Details'),
                icon: <NavigationPointer01 className="vertical-secondary-tab-item-icon flex-none" />,
            },
        ];

        // Client decision (Final wording) — tab renamed "Pickups & Drop-offs" -> "Items per Stop".
        // Only used for this tab name (grepped), so the i18n value itself was changed in place
        // rather than adding a new key.
        const pickupsDropoffsTab = {
            value: EDIT_QUOTE_TABS.PICKUPS_DROPOFFS,
            name: t('MyQuote.Pickups_And_Dropoffs'),
            icon: <SwitchVertical01 className="vertical-secondary-tab-item-icon flex-none" />,
        };

        if (classification === QUOTE_CLASSIFICATION.FTL) {
            // FTL: 3 tabs — matches QUOTE_STEPS FTL (3 steps)
            return [
                ...baseTabs,
                {
                    value: EDIT_QUOTE_TABS.SHIPMENT_DETAILS,
                    // Client decision (Final wording) — tab renamed to "Shipment Overview"; the
                    // step's own content/heading inside DimensionsTab etc. is untouched.
                    name: t('MyQuote.Tab_Shipment_Overview'),
                    icon: <Truck01 className="vertical-secondary-tab-item-icon flex-none" />,
                },
                pickupsDropoffsTab,
            ];
        }

        // LTL / Volume LTL: 4 tabs — matches QUOTE_STEPS LTL (4 steps)
        return [
            ...baseTabs,
            {
                value: EDIT_QUOTE_TABS.SHIPMENT_DETAILS,
                // Client decision (Final wording) — tab renamed to "Shipment Overview".
                name: t('MyQuote.Tab_Shipment_Overview'),
                icon: <Truck01 className="vertical-secondary-tab-item-icon flex-none" />,
            },
            {
                value: EDIT_QUOTE_TABS.DIMENSIONS,
                // Client decision (Final wording) — tab renamed to "Items & Dimensions".
                name: t('MyQuote.Tab_Items_Dimensions'),
                icon: <LayersThree02 className="vertical-secondary-tab-item-icon flex-none" />,
            },
            pickupsDropoffsTab,
        ];
    }, [classification, stopsFormData, t]);

    return (
        <>
            <CommonModal
                title={
                    isViewQuote
                        ? t('QuotingHub.Common.View_Quote_Details')
                        : t('QuotingHub.Common.Edit_Quote_Details_with_quote_id', {
                              quoteId: addressDetails?.code ?? '',
                              defaultValue: `Edit Quote Details - ${addressDetails?.code ?? ''}`,
                          })
                }
                handleClose={handleClose}
                size={' max-w-[1055px] h-[88vh] '}
                modalClassName="flex flex-col"
                primaryBtnText={isStatusWonOrLost ? '' : isViewQuote ? t('Common.Edit') : t('Common.Save_Changes')}
                secondaryBtnText={t('Common.Cancel')}
                primaryBtnDisabled={isViewQuote ? false : isUpdateLoading || (!isFormDirty && !isStopsDirty)}
                primaryBtnLoading={isUpdateLoading}
                primaryBtnOnClick={isViewQuote ? handleViewQuote : handleSubmit(onSubmit, handleError)}
                secondaryBtnOnClick={handleClose}
                ModalWithSidebar={true}
                ContentLeft={
                    <>
                        <TabButton
                            activeClassName="tab-box-vertical-active"
                            isModalTabVerticalSidebar={true}
                            isVerticalBoxTab={true}
                            tabArray={QUOTE_DETAIL_TABS}
                            isActive={activeTab}
                            handleOnClick={handleTabChange}
                        />
                    </>
                }
            >
                <div className="flex flex-1 overflow-auto">
                    <div className={`flex-1 px-4 py-4 overflow-auto custom-scrollbar-v2 scrollbar-hide ${activeTab === EDIT_QUOTE_TABS.DIMENSIONS ? `!pt-0` : `pt-4 space-y-3 `}`}>
                        <FormProvider {...methods}>
                            {activeTab === EDIT_QUOTE_TABS.SHIPPER_DETAILS ? <ShipperDetails /> : ''}
                            {stopsFormData.map((stop, index) => {
                                if (activeTab !== stopStepKey(stop.position)) return null;

                                return (
                                    <EditStopDetails
                                        key={stop.id}
                                        stop={stop}
                                        displayPosition={index + 1}
                                        onChange={(next) => {
                                            setStopsFormData((prev) => prev.map((s) => (s.id === next.id ? next : s)));
                                            setIsStopsDirty(true);

                                            if (stopAddressErrorId === next.id) {
                                                setStopAddressErrorId(null);
                                            }
                                        }}
                                        showAddressError={stopAddressErrorId === stop.id}
                                    />
                                );
                            })}
                            {activeTab === EDIT_QUOTE_TABS.CONSIGNEE_DETAILS ? <ConsigneeDetails /> : ''}
                            {activeTab === EDIT_QUOTE_TABS.SHIPMENT_DETAILS ? (
                                classification === QUOTE_CLASSIFICATION.LTL || classification === QUOTE_CLASSIFICATION.VOLUME_LTL ? (
                                    <ShipmentDetails />
                                ) : (
                                    <FtlShipmentDetails />
                                )
                            ) : (
                                ''
                            )}
                            {activeTab === EDIT_QUOTE_TABS.DIMENSIONS ? <DimensionsTab stopsFormData={stopsFormData} /> : ''}
                            {activeTab === EDIT_QUOTE_TABS.PICKUPS_DROPOFFS ? <PickupsDropoffsDetails /> : ''}
                        </FormProvider>
                    </div>
                </div>
            </CommonModal>
            {action.weightMismatch && <WeightMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['totalWeight'])} data={action.data} />}
            {action.linearFootageMismatch && <LinearFootageMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['linearFootage'])} data={action.data} />}
            {action.weightLinearFootageMismatch && (
                <WeightLinearFootageMismatchModal handleActionClose={handleActionClose} handleMismatch={handleMismatch(['totalWeight', 'linearFootage'])} data={action.data} />
            )}
            {action.tailgate && (
                <CommonModal
                    title={<h6 className="text-textSecondary text-lg font-semibold">{t('QuotingHub.Common.Tailgate_Over_Dimensions')}</h6>}
                    modalTitleDesc={
                        <p
                            className="text-grayLight600 text-sm font-normal"
                            dangerouslySetInnerHTML={{
                                __html: t('QuotingHub.QuotingDashboard.tailgate_warning', {
                                    length: FOOT,
                                    weight:
                                        action.data && action.data?.weightMeasurement === MEASUREMENT.WEIGHT1.value
                                            ? `<span style="color: #444CE7; font-weight: 600;">${LBS_MAX_VALUES.TAILGATE_MAX_WEIGHT}lbs</span>`
                                            : `<span style="color: #444CE7; font-weight: 600;">${KGS_MAX_VALUES.TAILGATE_MAX_WEIGHT}kgs</span>`,
                                }),
                            }}
                        />
                    }
                    modalHeaderClass="border-b-0 !px-6 !pt-6 !pb-5 !bg-transparent"
                    modalHeaderTextClass="!flex-col"
                    modalFooterClass="border-t-0 !bg-transparent"
                    closeBtnClass="-mt-2 -mr-2"
                    handleClose={handleTailgate(false)}
                    size="max-w-[420px]"
                    primaryBtnText={t('Common.confirm_yes')}
                    secondaryBtnText={t('Common.Modify_Selection')}
                    secondaryBtnOnClick={handleTailgate(false)}
                    primaryBtnOnClick={handleTailgate(true)}
                >
                    <></>
                </CommonModal>
            )}
        </>
    );
};

export default QuoteDetailModalNew;
